import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'

import PopupCloseButton from 'components/PopupCloseButton'
import useMediaQuery from 'hooks/useMediaQuery'
import { cn } from 'lib/utils'

import { centredIn, clampToViewport, type Point } from './floatingGeometry'

const ANIMATION_MS = 250

export interface FloatingPanelProps {
	open: boolean
	onClose: () => void
	/** Shown centred in the header bar, as the Chat popup's title is. */
	title: React.ReactNode
	/** The panel's accessible name. */
	ariaLabel: string
	/** Desktop width in pixels. */
	width?: number
	/**
	 * Where the panel's top-left corner opens, e.g. just under the control
	 * that opened it. Read once per open; kept on screen. Centred without it.
	 */
	anchor?: () => Point | null
	/** Label for the close button. */
	closeLabel?: string
	className?: string
	'data-testid'?: string
	children: React.ReactNode
}

/**
 * A floating window (MYD-644): the Chat popup's look and behaviour, for any
 * content.
 *
 * Not built on components/ui/dialog, for the same reasons as the Chat popup:
 * Dialog centres its child, puts a backdrop behind it and locks the page's
 * scroll, whereas this floats over a page that stays usable, and is MOVED by
 * dragging its header. It is kept fully on screen while dragged and when the
 * window is resized (floatingGeometry.ts, shared with Chat).
 *
 * It closes with the X or Escape, not on a click elsewhere: a panel the user
 * has dragged aside is one they mean to keep while they work.
 *
 * On a phone it is full-screen, as Chat is, since there is nowhere to drag it.
 *
 * Portalled to the body at the Chat popup's layer, above dialogs and sheets.
 * Position is explicit pixel left/top, so dragging and the open animation
 * cannot fight over `transform`.
 */
export function FloatingPanel({
	open,
	onClose,
	title,
	ariaLabel,
	width = 420,
	anchor,
	closeLabel = 'Close',
	className,
	'data-testid': testId,
	children,
}: FloatingPanelProps) {
	const isDesktop = useMediaQuery('(min-width: 768px)')
	const panelRef = useRef<HTMLDivElement>(null)
	const triggerRef = useRef<Element | null>(null)
	// Kept mounted through the exit transition so the panel animates out.
	const [isMounted, setIsMounted] = useState(false)
	const [isVisible, setIsVisible] = useState(false)
	const [position, setPosition] = useState<Point | null>(null)
	const [isDragging, setIsDragging] = useState(false)
	// Where in the header it was grabbed, so it does not jump on the first move.
	const dragOffset = useRef<{ dx: number; dy: number; width: number; height: number } | null>(null)

	useEffect(() => {
		if (open) {
			triggerRef.current = document.activeElement
			setIsMounted(true)
			// Next frame, so the enter transition has a start state to run from.
			const id = requestAnimationFrame(() => setIsVisible(true))
			return () => cancelAnimationFrame(id)
		}
		setIsVisible(false)
		const id = setTimeout(() => setIsMounted(false), ANIMATION_MS)
		return () => clearTimeout(id)
	}, [open])

	// Focus goes back to whatever opened it.
	useEffect(() => {
		if (open || !triggerRef.current) return
		;(triggerRef.current as HTMLElement).focus?.()
		triggerRef.current = null
	}, [open])

	useEffect(() => {
		if (!open) return
		const onKeyDown = (e: KeyboardEvent) => {
			if (e.key !== 'Escape') return
			e.stopPropagation()
			onClose()
		}
		document.addEventListener('keydown', onKeyDown)
		return () => document.removeEventListener('keydown', onKeyDown)
	}, [open, onClose])

	const size = () => {
		const rect = panelRef.current?.getBoundingClientRect()
		return { w: rect?.width || width, h: rect?.height || 0 }
	}

	// Placed on every open, before paint, so it never flashes at 0,0. Cleared
	// on close, so the next open starts from the anchor again.
	useLayoutEffect(() => {
		if (!isMounted || !isDesktop) {
			setPosition(null)
			return
		}
		const { w, h } = size()
		const start = anchor?.()
		setPosition(start ? clampToViewport(start, w, h) : centredIn(w, h))
		// The anchor is read once per open, on purpose.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isMounted, isDesktop])

	// A window resize, or content growing (loading -> loaded), can push a placed
	// panel off screen; pull it back.
	useEffect(() => {
		if (!isMounted || !isDesktop) return
		const keepOnScreen = () => {
			const { w, h } = size()
			setPosition(prev => (prev ? clampToViewport(prev, w, h) : prev))
		}
		window.addEventListener('resize', keepOnScreen)
		const observer = typeof ResizeObserver === 'undefined' || !panelRef.current ? null : new ResizeObserver(keepOnScreen)
		if (observer && panelRef.current) observer.observe(panelRef.current)
		return () => {
			window.removeEventListener('resize', keepOnScreen)
			observer?.disconnect()
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isMounted, isDesktop])

	const handleDragStart = useCallback(
		(e: React.PointerEvent) => {
			if (!isDesktop) return
			// Let the header's buttons be buttons.
			if ((e.target as HTMLElement).closest('button')) return
			const rect = panelRef.current?.getBoundingClientRect()
			if (!rect) return
			dragOffset.current = { dx: e.clientX - rect.left, dy: e.clientY - rect.top, width: rect.width, height: rect.height }
			setIsDragging(true)
			// Captured on the header, so a fast drag that outruns the cursor
			// still delivers its moves here.
			;(e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId)
		},
		[isDesktop],
	)

	const handleDragMove = useCallback((e: React.PointerEvent) => {
		const offset = dragOffset.current
		if (!offset) return
		e.preventDefault()
		setPosition(clampToViewport({ x: e.clientX - offset.dx, y: e.clientY - offset.dy }, offset.width, offset.height))
	}, [])

	const handleDragEnd = useCallback((e: React.PointerEvent) => {
		if (!dragOffset.current) return
		dragOffset.current = null
		setIsDragging(false)
		;(e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId)
	}, [])

	if (!isMounted) return null

	const panel = (
		<div
			ref={panelRef}
			data-testid={testId}
			role="dialog"
			// Modal only on a phone, where it covers the screen; on desktop the
			// page behind stays usable.
			aria-modal={!isDesktop}
			aria-label={ariaLabel}
			className={cn(
				'fixed z-[10003] flex flex-col overflow-hidden bg-white ease-out',
				// Position is animated only on open: transitioning left/top would
				// make the panel lag a frame behind the cursor while dragging.
				isDragging ? 'transition-none' : 'transition-[opacity,transform] duration-[250ms]',
				isDesktop
					? [
							'max-h-[calc(100vh-6rem)] rounded-2xl border border-gray-200 shadow-2xl',
							isVisible ? 'scale-100 opacity-100' : 'scale-95 opacity-0',
							// Hidden until placed, so it cannot flash at 0,0.
							position ? 'visible' : 'invisible',
						]
					: ['inset-0', isVisible ? 'translate-y-0' : 'translate-y-full'],
				className,
			)}
			style={isDesktop ? { width: `${width}px`, ...(position && { left: `${position.x}px`, top: `${position.y}px` }) } : undefined}
		>
			<div
				data-testid={testId ? `${testId}-header` : undefined}
				onPointerDown={handleDragStart}
				onPointerMove={handleDragMove}
				onPointerUp={handleDragEnd}
				onPointerCancel={handleDragEnd}
				className={cn(
					'flex shrink-0 items-center gap-1 border-b border-gray-200 px-2 py-2',
					'pt-[max(0.5rem,env(safe-area-inset-top))] md:pt-2',
					// select-none while draggable, or a drag leaves the title highlighted.
					isDesktop && ['select-none', isDragging ? 'cursor-grabbing' : 'cursor-grab'],
				)}
			>
				{/* Balances the close button, so the title sits in the middle. */}
				<div className="w-9 shrink-0" aria-hidden="true" />
				<h2 className="m-0 min-w-0 flex-1 truncate text-center text-[16px] font-semibold text-gray-900">{title}</h2>
				<PopupCloseButton onClose={onClose} ariaLabel={closeLabel} className="-mr-1.5" />
			</div>
			<div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
		</div>
	)

	return ReactDOM.createPortal(panel, document.body)
}

export default FloatingPanel
